import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from 'styled-components';

import { AgentAssistanceConfigContextProvider } from 'components/contexts/agent_assistance_config_context';
import analytics from 'scripts/lib/analytics';
import { AudioPlaybackProvider } from 'components/contexts/audio_playback';
import { CurrentAgentProvider } from 'components/contexts/current_agent';
import { ExecuteActionProvider } from 'components/lib/connect';
import ErrorBoundary from 'components/common/utilities/error_boundary';
import FakeWebRTCStarter from 'components/fake_webrtc_starter';
import { HotkeysProvider } from 'components/contexts/hotkeys';
import { IsFeatureEnabledProvider } from 'components/contexts/is_feature_enabled';
import { IsTeamFlagEnabledProvider } from 'components/contexts/is_team_flag_enabled';
import NewRootContent from 'components/root/root_content';
import theme from 'scripts/presentation/themes/default';

export function Root({ binding }) {
  // useTranslation re-renders on languageChanged; keying by language remounts the
  // tree so surfaces rendered via the non-reactive i18n singleton (i18n.t in class
  // components and helpers) pick up the new language too.
  const { i18n } = useTranslation();
  return (
    <ErrorBoundary errorMeta={{ errorBoundary: 'Root' }} key={i18n.language} renderError={() => <RootError />}>
      <FakeWebRTCStarter />
      <RootProviders>
        <NewRootContent binding={binding} />
      </RootProviders>
    </ErrorBoundary>
  );
}

function RootError() {
  const { t } = useTranslation();
  const onClick = useCallback(() => location.reload(), []);
  useEffect(() => {
    analytics.track('Whoops Screen Shown', {
      componentName: 'root',
    });
  }, []);

  return (
    <div className="root-errorBoundary">
      <div>{t('Whoops! Something went wrong.')}</div>
      <div className="root-errorBoundary-continue" onClick={onClick}>
        {t('Click here to reload the page')}
      </div>
    </div>
  );
}

export function RootProviders({ children }) {
  return (
    <ThemeProvider theme={theme}>
      <HotkeysProvider>
        <ExecuteActionProvider>
          <IsFeatureEnabledProvider>
            <IsTeamFlagEnabledProvider>
              <AgentAssistanceConfigContextProvider>
                <CurrentAgentProvider>
                  <AudioPlaybackProvider>{children}</AudioPlaybackProvider>
                </CurrentAgentProvider>
              </AgentAssistanceConfigContextProvider>
            </IsTeamFlagEnabledProvider>
          </IsFeatureEnabledProvider>
        </ExecuteActionProvider>
      </HotkeysProvider>
    </ThemeProvider>
  );
}

export default Root;
